import { useState, useRef, useEffect, useCallback } from 'react';

// Feedback Box — a small side-channel for friends/colleagues testing the app.
// Fully isolated: it only POSTs to /api/feedback. No Claude, no memory, no app state.
//
// Two exports:
//   <FeedbackPill onClick={…} />     — the pill trigger, hosted in the header banner.
//   <FeedbackWidget open onClose />  — the controlled panel (rendered once, at App level).

const ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export function FeedbackPill({ onClick, className = '' }) {
  return (
    <button className={`fb-pill ${className}`.trim()} onClick={onClick}
      aria-label="Send feedback" title="Send feedback">
      {ICON}
      <span>Feedback</span>
    </button>
  );
}

// Resize a screenshot client-side so uploads stay reasonable. Caps width at ~1600px and
// re-encodes as JPEG. Screenshots lose transparency in JPEG — fine for feedback.
function fileToResizedDataUrl(file, maxW = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxW / img.width || 1);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      try { resolve(canvas.toDataURL('image/jpeg', quality)); }
      catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('bad image')); };
    img.src = url;
  });
}

// Best-effort snapshot of where the user is. Never throws — a surface can register
// window.__b360FeedbackContext() to add its active tab / expanded state.
function captureContext() {
  const ctx = {
    pathname: window.location.pathname,
    timestamp: new Date().toISOString(),
    viewport: { w: window.innerWidth, h: window.innerHeight },
    userAgent: navigator.userAgent,
  };
  try {
    const extra = typeof window.__b360FeedbackContext === 'function'
      ? window.__b360FeedbackContext()
      : null;
    if (extra && typeof extra === 'object') Object.assign(ctx, extra);
  } catch (_) { /* best-effort */ }
  return ctx;
}

export default function FeedbackWidget({ open, onClose }) {
  const [text, setText] = useState('');
  const [image, setImage] = useState(null); // resized data-url
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const panelRef = useRef(null);
  const textRef = useRef(null);

  const reset = useCallback(() => {
    setText(''); setImage(null); setError(null); setSent(false); setSending(false);
  }, []);

  const close = useCallback(() => { reset(); onClose(); }, [reset, onClose]);

  // Focus the textarea on open; clear fields whenever the panel closes.
  useEffect(() => { if (open && textRef.current) textRef.current.focus(); }, [open]);
  useEffect(() => { if (!open) reset(); }, [open, reset]);

  // Close on outside click — via a passive listener, never a capturing backdrop, so it
  // never blocks interaction with the rest of the app.
  useEffect(() => {
    if (!open) return;
    function onDocDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) close();
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open, close]);

  async function handleFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    try { setImage(await fileToResizedDataUrl(file)); }
    catch { setError('Could not read that image.'); }
  }

  function onPaste(e) {
    const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'));
    if (item) { e.preventDefault(); handleFile(item.getAsFile()); }
  }

  function onDrop(e) {
    e.preventDefault();
    const file = [...(e.dataTransfer?.files || [])].find(f => f.type.startsWith('image/'));
    if (file) handleFile(file);
  }

  async function send() {
    if (sending) return;
    setSending(true); setError(null);
    const context = captureContext();
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: text.trim(),
          page: context.tab || context.pathname,
          context,
          image,
        }),
      });
      if (!res.ok) throw new Error('failed');
      setSent(true);
      setTimeout(() => close(), 1500); // brief confirmation, then auto-dismiss
    } catch {
      setError('Could not send — try again.');
      setSending(false);
    }
  }

  if (!open) return null;

  const canSend = (text.trim().length > 0 || !!image) && !sending && !sent;

  return (
    <div
      ref={panelRef}
      className="fb-panel"
      onPaste={onPaste}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <div className="fb-panel-head">
        <span className="fb-title">Feedback</span>
        <button className="fb-x" aria-label="Close" onClick={close}>×</button>
      </div>

      {sent ? (
        <div className="fb-thanks">Thanks — got it</div>
      ) : (
        <>
          {image && (
            <div className="fb-thumb-wrap">
              <img className="fb-thumb" src={image} alt="attachment preview" />
              <button className="fb-thumb-x" aria-label="Remove image"
                onClick={() => setImage(null)}>×</button>
            </div>
          )}

          <textarea
            ref={textRef}
            className="fb-textarea"
            placeholder="What's working, what's not? Paste a screenshot too (Cmd/Ctrl+V)."
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
          />

          {error && <div className="fb-error">{error}</div>}

          <div className="fb-actions">
            <span className="fb-hint">{image ? 'Image attached' : ''}</span>
            <button className="fb-send" disabled={!canSend} onClick={send}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
